import { InputAdornment } from "@mui/material";
import TextField, { TextFieldProps } from "@mui/material/TextField";
import React, { useEffect, useRef } from "react";
import { Decimal } from "../../decimal";

import '../../styles/shared/numberField.scss';

type NumberFieldProps = TextFieldProps & {
	isCurrency?: boolean;
	precision?: number;
	min?: number;
	max?: number;
	step?: number;
}

const NumberField: React.FunctionComponent<NumberFieldProps> = ({ isCurrency, precision = 4, value = '0', min, max, step = 1, onBlur, onChange, onKeyDown, onWheel, ...props }) => {
	const scrollRef = useRef(null);
	const [inputValue, setInputValue] = React.useState(value);
	const [lastOnBlurValue, setLastOnBlurValue] = React.useState(value);

	useEffect(() => {
		const inputElement = scrollRef.current?.querySelector('input');
		if (inputElement) {
			inputElement.addEventListener('wheel', preventScroll, { passive: false });
		}

		return () => {
			if (inputElement) {
				inputElement.removeEventListener('wheel', preventScroll);
			}
		};
	}, []);

	useEffect(() => {
		setInputValue(restrictToPrecision(value));
	}, [value]);

	const preventScroll = (event) => {
		if (scrollRef && scrollRef.current) {
			event.preventDefault();
		}
	}

	const roundToPrecision = (value: number, decimalDigits: number) => {
		return new Decimal(value).mul(Math.pow(10, decimalDigits)).round().div(Math.pow(10, decimalDigits)).toNumber();
	}

	const restrictToPrecision = (value: unknown): string => {
		if (typeof value !== 'string' && typeof value !== 'number') return '0';
		const stringValue = value.toString();
		if (stringValue.trim() === '') return '0';
		if (precision == 0) return Math.floor(+stringValue).toString();
		const parts = stringValue.split('.');
		if (parts.length === 2 && parts[1].length > precision) {
			return parts[0] + '.' + parts[1].substring(0, precision);
		}
		return stringValue;
	}

	const handlBlur = (event: React.FocusEvent<HTMLInputElement>) => {
		event.preventDefault();
		if (!isWithinBounds(+inputValue)) {
			setInputValue(lastOnBlurValue);
			return;
		}
		else {
			setLastOnBlurValue(inputValue);
		}
		invokeOnChange(inputValue as string);
		onBlur?.(event);
	}

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		event.preventDefault();
		const newValue = restrictToPrecision(event.target.value);
		setInputValue(newValue);
		invokeOnChange(newValue);
	}

	const handleWheel = (event: React.WheelEvent<HTMLDivElement>) => {
		const delta = Math.sign(event.deltaY) * Math.abs(step);
		const newValue = restrictToPrecision(roundToPrecision(+inputValue - delta, precision).toString());
		if (!isWithinBounds(+newValue)) return;
		setInputValue(newValue);
		invokeOnChange(newValue);
		onWheel?.(event);
	}

	const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
			event.preventDefault();
			const newValue = restrictToPrecision(roundToPrecision(+inputValue + (event.key === 'ArrowUp' ? Math.abs(step) : -Math.abs(step)), precision).toString());
			if (!isWithinBounds(+newValue)) return;
			setInputValue(newValue);
			invokeOnChange(newValue);
		}
		else if (event.key === '-') {
			event.preventDefault();
			if (min === undefined || min < 0) {
				const newValue = restrictToPrecision((+inputValue * -1).toString());
				if (!isWithinBounds(+newValue)) return;
				setInputValue(newValue);
				invokeOnChange(newValue);				
			}
		}		
		else if (precision == 0 && event.key === '.') {
			event.preventDefault();
		}
		else {
			onKeyDown?.(event);
		}		
	}

	const isWithinBounds = (value: number): boolean => {
		if (min !== undefined && value < min) return false;
		if (max !== undefined && value > max) return false;
		return true;
	}

	const invokeOnChange = (newValue: string) => {
		const syntheticEvent = {
			target: { name: props.name, value: newValue }
		} as React.ChangeEvent<HTMLInputElement>;
		onChange?.(syntheticEvent);
	}

	return (
		<TextField
			{...props}
			className="number-field"
			value={inputValue}
			type="number"
			ref={scrollRef}
			onBlur={handlBlur}
			onChange={handleChange}
			onKeyDown={handleKeyDown}
			onWheel={handleWheel}
			InputProps={{
				...props.InputProps,
				...(isCurrency == true && { startAdornment: <InputAdornment position="start"><strong>$</strong></InputAdornment> }),
				classes: { input: 'no-number-spinner' }
			}}
		></TextField>
	);
}

export default NumberField;
